/**
 * Hooks for the Sharing domain.
 *
 * Replaces Wized requests: get_course_share, create_course_share,
 *   get_playlist_shares, get_shared_playlist_data, create_playlist_share
 * Replaces Wized elements: share_course, share_tutorial, share_tutorial_btn,
 *   share_tutorial_input, share_course_copy_btn, share_playlist_btn,
 *   share_playlist_input, share_playlist_copy_btn, shared_playlist_name,
 *   shared_course_nav_title, shared_course_video_item, etc.
 */

import { useAsync, useMutation } from "./useAsync";
import * as api from "../lib/api";
import { useAuth } from "../lib/auth";
import type { CourseShareRow, PlaylistShareRow } from "../lib/schema";
import { buildShareTutorialUrl, buildSharePlaylistUrl, isExpired } from "../lib/helpers";

// ---------------------------------------------------------------------------
// Course sharing
// ---------------------------------------------------------------------------

/** Fetch the current user's course share records. */
export function useCourseShares() {
  const { user } = useAuth();
  return useAsync(
    () => api.getCourseShares(user!.id),
    user ? [user.id] : null,
  );
}

/** Mutation: create a share token for a course. */
export function useCreateCourseShare() {
  const { user } = useAuth();
  return useMutation(async (courseId: string) => {
    if (!user) return { data: null, error: { message: "Not authenticated" } };
    return api.createCourseShare(user.id, courseId);
  });
}

export function isCourseShareActive(share: Pick<CourseShareRow, "expires_at">): boolean {
  return !share.expires_at || new Date(share.expires_at).getTime() > Date.now();
}

/** Find the user's latest active share for a course (if any). */
export function findCourseShare(
  shares: CourseShareRow[] | null,
  userId: string,
  courseId: string,
): CourseShareRow | undefined {
  return shares
    ?.filter(
      (s) =>
        s.shared_by === userId &&
        s.course_id === courseId &&
        isCourseShareActive(s),
    )
    .sort(
      (a, b) =>
        new Date(b.shared_at).getTime() - new Date(a.shared_at).getTime(),
    )[0];
}

/** Get the shareable URL for a course share token. */
export function getCourseShareUrl(
  shares: CourseShareRow[] | null,
  userId: string,
  courseId: string,
): string | null {
  const share = findCourseShare(shares, userId, courseId);
  return share ? buildShareTutorialUrl(share.share_token) : null;
}

// ---------------------------------------------------------------------------
// Playlist sharing
// ---------------------------------------------------------------------------

/** Fetch the current user's playlist share records. */
export function usePlaylistShares() {
  const { user } = useAuth();
  return useAsync(
    () => api.getPlaylistShares(user!.id),
    user ? [user.id] : null,
  );
}

/** Fetch shared playlist data (with playlist names). */
export function useSharedPlaylistData() {
  return useAsync(() => api.getSharedPlaylistData(), []);
}

/** Mutation: create a share invitation for a playlist. */
export function useCreatePlaylistShare() {
  const { user } = useAuth();
  return useMutation(async (playlistId: string) => {
    if (!user) return { data: null, error: { message: "Not authenticated" } };
    return api.createPlaylistShare(user.id, playlistId);
  });
}

export function isPlaylistShareActive(share: Pick<PlaylistShareRow, "expires_at">): boolean {
  return !share.expires_at || !isExpired(share.expires_at);
}

/** Find the user's latest active share for a playlist (if any). */
export function findPlaylistShare(
  shares: PlaylistShareRow[] | null,
  userId: string,
  playlistId: string,
): PlaylistShareRow | undefined {
  return shares
    ?.filter(
      (s) =>
        s.shared_by === userId &&
        s.playlist_id === playlistId &&
        isPlaylistShareActive(s),
    )
    .sort(
      (a, b) =>
        new Date(b.shared_at).getTime() - new Date(a.shared_at).getTime(),
    )[0];
}

/** Get the shareable URL for a playlist share token. */
export function getPlaylistShareUrl(
  shares: PlaylistShareRow[] | null,
  userId: string,
  playlistId: string,
): string | null {
  const share = findPlaylistShare(shares, userId, playlistId);
  return share ? buildSharePlaylistUrl(share.invitation_token) : null;
}
